import React, { useMemo, useState, useCallback } from "react";
import Select, { components, SingleValue } from "react-select";

type BaseOption = {
    label: string;
};

type PagedSelectProps<TOption extends BaseOption> = {
    allOptions: TOption[];
    value: TOption | null;
    onChange: React.Dispatch<React.SetStateAction<TOption | null>>;
    pageSize?: number;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
};

export default function PageSelect<TOption extends BaseOption>({
    allOptions,
    value,
    onChange,
    pageSize = 10,
    placeholder = "Search and select...",
    className = "min-w-[250px]",
    disabled = false,
}: PagedSelectProps<TOption>) {
    const [inputValue, setInputValue] = useState("");
    const [visibleCount, setVisibleCount] = useState(pageSize);

    const filteredAll = useMemo(() => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return allOptions;
        return allOptions.filter((o) => o.label.toLowerCase().includes(q));
    }, [allOptions, inputValue]);

    const visibleOptions = useMemo(
        () => filteredAll.slice(0, visibleCount),
        [filteredAll, visibleCount]
    );

    const hasMore = visibleCount < filteredAll.length;

    const loadMore = useCallback(() => {
        if (disabled) return;
        setVisibleCount((c) => c + pageSize);
    }, [pageSize, disabled]);

    const MenuList = (props: any) => (
        <components.MenuList {...props}>
            {props.children}
            {!disabled && hasMore && (
                <div style={{ padding: 8, borderTop: "1px solid #eee" }}>
                    <button
                        type="button"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            loadMore();
                        }}
                        style={{
                            width: "100%",
                            padding: "8px 10px",
                            borderRadius: 6,
                            border: "1px solid #ddd",
                            background: "#fff",
                            cursor: "pointer",
                            fontWeight: 600,
                        }}
                    >
                        See more
                    </button>
                </div>
            )}
        </components.MenuList>
    );

    return (
        <Select<TOption, false>
            options={visibleOptions}
            value={value}
            onChange={(val: SingleValue<TOption>) => onChange(val ?? null)}
            isClearable={!disabled}
            placeholder={placeholder}
            className={className}
            components={{ MenuList }}
            inputValue={inputValue}
            isDisabled={disabled}
            onInputChange={(val) => {
                if (disabled) return;
                setInputValue(val);
                setVisibleCount(pageSize);
            }}
            filterOption={() => true}
        />
    );
}